/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { tabs } from 'components/layouts/Tab';
import { IconButton } from 'components/buttons/IconSVG';

import { NormalButton, IcoButton } from 'components/buttons/ButtonStyle';
import {
  SetupHome,
  ProjectListWrap,
  ProjectList,
  StatusNumber,
  ListItemWrap,
  ListItem,
  ProjectView,
  ProjectKanban,
  KanbanList,
} from 'components/home/SetupHome';
import _ from 'lodash';
import { findSetupHomePorject } from 'apis/setup/home/SetupHome';
import { useLoading } from 'components/process/Loading';
import { SetupHomeProject } from 'models/setup/home/SetupProject';
import { useTranslation } from 'react-i18next';
import ProgressPieChart from 'components/chart/ProgressPieChart';
import useSessionStore from 'stores/useSessionStore';
import { useNavigate } from 'react-router-dom';

type GroupObject = Record<string, Record<string, any>>;

interface FilterInfo {
  bildPlntCd: string;
  ctryCd: string;
  ctryNm: string;
}

const SetupHomePage = () => {
  const CTRY_INFO = {
    PL: { plntList: {}, count: 0, style: { left: '123px', top: '0' } },
    CN: { plntList: {}, count: 0, style: { left: '359px', top: '168px' } },
    KR: { plntList: {}, count: 0, style: { left: '535px', top: '36px' } },
    US: { plntList: {}, count: 0, style: { left: '890px', top: '25px' } },
  };
  const SCROLL_SIZE = 150;

  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { userId } = useSessionStore();
  const navigate = useNavigate();

  const filterAreaRef = useRef<HTMLUListElement>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [projectList, setProjectList] = useState<SetupHomeProject[]>([]);
  const [filterList, setFilterList] = useState<FilterInfo[]>([]);
  const [toggleView, setToggleView] = useState<boolean>(false);

  const groupObj: GroupObject = useMemo(() => {
    const result = projectList.reduce(
      (acc: any, item: SetupHomeProject) => {
        acc[item.ctryGrCd].count = projectList.filter((o) => o.ctryGrCd === item.ctryGrCd).length;

        if (!acc[item.ctryGrCd].plntList[item.bildPlntCd]) {
          acc[item.ctryGrCd].plntList[item.bildPlntCd] = {
            ctryGrCd: item.ctryGrCd,
            ctryCd: item.ctryCd,
            ctryNm: item.ctryNm,
            bildPlntCd: item.bildPlntCd,
            newCnt: projectList.filter(
              (o) => o.bildPlntCd === item.bildPlntCd && o.prjDivsCd === 'NEW'
            ).length,
            expCnt: projectList.filter(
              (o) => o.bildPlntCd === item.bildPlntCd && o.prjDivsCd === 'EXP'
            ).length,
          };
        }

        return acc;
      },
      { ...CTRY_INFO }
    );
    return result;
  }, [projectList]);

  const countList = useMemo(() => {
    const result = {
      totalCnt: 0,
      myCnt: 0,
      newCnt: 0,
      expCnt: 0,
      completeCnt: 0,
    };

    for (const o of projectList) {
      result.totalCnt++;
      if (o.repMgrId === userId) result.myCnt++;
      if (o.prjDivsCd === 'NEW') result.newCnt++;
      if (o.prjDivsCd === 'EXP') result.expCnt++;
    }
    return result;
  }, [projectList]);

  const handleChange = (event: any, newValue: React.SetStateAction<number>) => {
    setCurrentTab(newValue);
  };

  const getProjectList = () => {
    openLoading(true);
    findSetupHomePorject()
      .then((res: SetupHomeProject[]) => {
        setProjectList(res?.map((o) => ({ ...o })) || []);
      })
      .finally(() => openLoading(false));
  };

  const a11ySubProps = (index: number) => {
    return {
      id: `sub-tab-${index}`,
      'aria-controls': `sub-tabpanel-${index}`,
    };
  };

  const fnToggleProjectView = (idx) => {
    setCurrentTab(idx);
    setToggleView(true);
  };

  const fnManageFilter = (_obj) => {
    const obj: FilterInfo = {
      bildPlntCd: _obj.bildPlntCd,
      ctryCd: _obj.ctryCd,
      ctryNm: _obj.ctryNm,
    };
    setFilterList((prev) =>
      prev.some((o) => o.bildPlntCd === obj.bildPlntCd)
        ? prev.filter((o) => o.bildPlntCd !== obj.bildPlntCd)
        : prev.concat(obj)
    );
    setToggleView(true);
  };

  const fnFilterScroll = (num) => {
    if (filterAreaRef.current) {
      filterAreaRef.current.scrollBy({
        left: num,
        behavior: 'smooth',
      });
    }
  };

  const fnMoveSummary = (elmPrjId = '') => {
    navigate(`/setup/home/project-summary`, {
      state: { elmPrjId },
    });
  };

  useEffect(() => {
    // initialize data
    getProjectList();
  }, []);

  return (
    <SetupHome>
      <div className="ProjectStatusWrap">
        <div className="title">
          <h3>{t('setup.label.설비셋업관리', '설비셋업관리')}</h3>
          <p>
            {t('setup.label.LG 에너지솔루션 FA 설비 셋업관리', 'LG 에너지솔루션 FA 설비 셋업관리')}
          </p>
        </div>
        <div className="ProjectStatus">
          <div className="total">
            <h4>{t('setup.label.현재 프로젝트', '현재 프로젝트')}</h4>
            <ul>
              <li role="button" onClick={() => fnToggleProjectView(0)}>
                TOTAL <span>{countList.totalCnt}</span>
              </li>
              <li role="button" onClick={() => fnToggleProjectView(1)}>
                MY <span>{countList.myCnt}</span>
              </li>
            </ul>
          </div>
          <div className="status">
            <ul>
              <li role="button" onClick={() => fnToggleProjectView(2)}>
                {t('setup.label.신축', '신축')} <span>{countList.newCnt}</span>
              </li>
              <li role="button" onClick={() => fnToggleProjectView(3)}>
                {t('setup.label.증설', '증설')} <span>{countList.expCnt}</span>
              </li>
              <li role="button" onClick={() => fnToggleProjectView(4)}>
                {t('setup.label.완료', '완료')} <span>{countList.completeCnt}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <ProjectListWrap>
        {Object.entries(groupObj).map(([ctryGrCd, obj]: [string, any]) => {
          return (
            <ProjectList key={ctryGrCd} count={obj.plntList.length} style={obj.style}>
              <ListItemWrap count={obj.plntList.length}>
                {Object.entries(obj.plntList)
                  .filter((arr, idx) => idx < 10)
                  .map(([bildPlntCd, obj2]: [string, any]) => {
                    return (
                      <ListItem
                        key={bildPlntCd}
                        onClick={() => fnManageFilter(obj2)}
                        selected={filterList.some((o) => o.bildPlntCd === bildPlntCd)}
                      >
                        <strong>{`${obj2.ctryNm}-${bildPlntCd}`}</strong>
                        <span>
                          <NormalButton css={IconButton.button} size="sm" className="icoNone">
                            {t('setup.label.신축', '신축') + obj2.newCnt}
                          </NormalButton>
                          <NormalButton css={IconButton.button} size="sm" className="icoNone">
                            {t('setup.label.증설', '증설') + obj2.expCnt}
                          </NormalButton>
                        </span>
                      </ListItem>
                    );
                  })}
              </ListItemWrap>
              <StatusNumber size={obj.count}>{obj.count}</StatusNumber>
              {Object.entries(obj.plntList).length >= 10 && (
                <ProjectList
                  key={ctryGrCd}
                  className="reverse"
                  count={obj.plntList.length}
                  style={obj.style}
                >
                  <ListItemWrap count={obj.plntList.length}>
                    {Object.entries(obj.plntList)
                      .filter((arr, idx) => idx >= 10)
                      .map(([bildPlntCd, obj2]: [string, any]) => {
                        return (
                          <ListItem
                            key={bildPlntCd}
                            onClick={() => fnManageFilter(obj2)}
                            selected={filterList.some((o) => o.bildPlntCd === bildPlntCd)}
                          >
                            <strong>{`${obj2.ctryNm}-${bildPlntCd}`}</strong>
                            <span>
                              <NormalButton css={IconButton.button} size="sm" className="icoNone">
                                {t('setup.label.신축', '신축') + obj2.newCnt}
                              </NormalButton>
                              <NormalButton css={IconButton.button} size="sm" className="icoNone">
                                {t('setup.label.증설', '증설') + obj2.expCnt}
                              </NormalButton>
                            </span>
                          </ListItem>
                        );
                      })}
                  </ListItemWrap>
                </ProjectList>
              )}
            </ProjectList>
          );
        })}
      </ProjectListWrap>

      <ProjectView show={toggleView}>
        <div className="header">
          <h4>{t('setup.label.프로젝트', '프로젝트')}</h4>
          <div className="btn">
            <IcoButton css={IconButton.button} className="popup"></IcoButton>
            <span>List</span>
            <IcoButton
              css={IconButton.button}
              className="cancel"
              onClick={() => setToggleView(!toggleView)}
            ></IcoButton>
          </div>
        </div>
        <div className="tabWrap">
          <Tabs value={currentTab} onChange={handleChange} css={tabs.anotherTab}>
            <Tab
              label={
                <span>
                  {t('setup.label.총', '총')} <em>{countList.totalCnt}</em>
                </span>
              }
              {...a11ySubProps(0)}
            />
            <Tab
              label={
                <span>
                  My <em>{countList.myCnt}</em>
                </span>
              }
              {...a11ySubProps(1)}
            />
            <Tab
              label={
                <span>
                  {t('setup.label.신축', '신축')} <em>{countList.newCnt}</em>
                </span>
              }
              {...a11ySubProps(2)}
            />
            <Tab
              label={
                <span>
                  {t('setup.label.증설', '증설')} <em>{countList.expCnt}</em>
                </span>
              }
              {...a11ySubProps(3)}
            />
            <Tab
              label={
                <span>
                  {t('setup.label.완료', '완료')} <em>{countList.completeCnt}</em>
                </span>
              }
              {...a11ySubProps(4)}
            />
          </Tabs>
        </div>
        <div className="filtterWrap">
          <IcoButton css={IconButton.button} className="filterOff"></IcoButton>
          <span>{t('setup.label.필터', '필터')}</span>
          <div className="filterList">
            <IcoButton
              css={IconButton.button}
              className="left"
              onClick={() => fnFilterScroll(-SCROLL_SIZE)}
            ></IcoButton>
            <ul ref={filterAreaRef}>
              {filterList.map((o, i) => {
                return (
                  <li key={i}>
                    {`${o.ctryNm}-${o.bildPlntCd} `}
                    <IcoButton
                      css={IconButton.button}
                      className="cancel"
                      onClick={() => fnManageFilter(o)}
                    ></IcoButton>
                  </li>
                );
              })}
            </ul>
            <IcoButton
              css={IconButton.button}
              className="right"
              onClick={() => fnFilterScroll(SCROLL_SIZE)}
            ></IcoButton>
          </div>
        </div>
        <CustomTabPanel index={0}>
          <ProjectKanban>
            {projectList
              .filter((o) => {
                if (currentTab === 1) {
                  return o.repMgrId === userId;
                } else if (currentTab === 2) {
                  return o.prjDivsCd === 'NEW';
                } else if (currentTab === 3) {
                  return o.prjDivsCd === 'EXP';
                } else if (currentTab === 4) {
                  return o.prjDivsCd === 'CPL';
                } else {
                  return true;
                }
              })
              .filter((o) =>
                filterList.length > 0 ? filterList.some((x) => x.bildPlntCd === o.bildPlntCd) : true
              )
              .map((item, idx) => {
                return (
                  <KanbanList key={`${item.elmPrjId}${idx}`}>
                    <div className="header">
                      <span className="status">{item.prjDivsNm}</span>
                      <h5>{item.elmPrjNm}</h5>
                      <span>{}</span>
                      <span>{`${item.ivesStDt} ~ ${item.ivesEndDt}`}</span>
                    </div>
                    <div className="user">
                      <span className="photo"></span>
                      <em className="name">{item.repMgrNm}</em>
                    </div>
                    <div className="etc">
                      <span>{t('setup.label.진행률', '진행률')}</span>
                      <span className="Graph">
                        <ProgressPieChart
                          progress={parseFloat(item.prcdRt) || 0}
                          width={32}
                          height={32}
                          fontSize={12}
                        />
                      </span>
                      <IcoButton
                        css={IconButton.button}
                        className="popup"
                        onClick={() => fnMoveSummary(item.elmPrjId)}
                      ></IcoButton>
                    </div>
                  </KanbanList>
                );
              })}
          </ProjectKanban>
        </CustomTabPanel>
      </ProjectView>
    </SetupHome>
  );
};

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
};

function CustomTabPanel(props: { [x: string]: any; children: any; index: any }) {
  const { children, index } = props;

  return (
    <div role="tabpanel" id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`}>
      <div>{children}</div>
    </div>
  );
}

export default SetupHomePage;
